/** Page-level composition helpers shared by every admin page. */
import { api } from '@agency/shared';
import { Button, Callout, Chip, Code, Dialog, EmptyState, Field, Input, Select, Textarea, fmtTimestamp, fmtRelative, type ButtonProps, type SelectOption } from '@agency/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, shortId } from '../lib/api.js';
import { describeError } from '../lib/mutate.js';
import { useSession } from '../lib/session.js';
import { useApi } from '../lib/useApi.js';

/** Page container: max 1640, px-8 py-8. */
export function Page({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`mx-auto w-full max-w-[1640px] px-8 py-8 ${className}`}>{children}</div>;
}

/** 12-column grid with 24px gutters. */
export function Grid({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`mt-6 grid grid-cols-12 gap-6 ${className}`}>{children}</div>;
}

/** Footer meta row (DESIGN_SYSTEM 10.1). */
export function FooterMeta({ left, right }: { left: string; right?: string }) {
  return (
    <div className="mt-8 flex items-center justify-between border-t border-hair pt-4 font-mono text-2xs uppercase tracking-[0.18em] text-mute">
      <span>{left}</span>
      {right ? <span>{right}</span> : null}
    </div>
  );
}

/** Error callout with request id and retry. */
export function ErrorCallout({ error, onRetry, className }: { error: ApiError | Error | null | undefined; onRetry?: () => void; className?: string }) {
  if (!error) return null;
  const apiError = ApiError.from(error);
  const title = apiError.code === 'forbidden' ? 'Not permitted' : apiError.isNetwork ? 'API unreachable' : 'Request failed';
  return (
    <Callout tone="critical" title={title} className={className} actions={onRetry ? <Button variant="tertiary" icon={RefreshCw} onClick={onRetry}>Retry</Button> : undefined}>
      <span>{apiError.message}</span>
      {apiError.requestId ? (
        <span className="ml-2 font-mono text-2xs text-mute">
          REQUEST <Code>{apiError.requestId}</Code>
        </span>
      ) : null}
    </Callout>
  );
}

export { describeError };

/** Mono timestamp `YYYY-MM-DD HH:MMZ` with a relative title. */
export function Stamp({ value, relative = false, className = '' }: { value: string | null | undefined; relative?: boolean; className?: string }) {
  if (!value) return <span className={`font-mono text-mute ${className}`}>--</span>;
  return (
    <span title={relative ? fmtTimestamp(value) : fmtRelative(value)} className={`font-mono tabular-nums ${className}`}>
      {relative ? fmtRelative(value) : fmtTimestamp(value)}
    </span>
  );
}

/** Mono id cell that keeps the original case; optional short form with full value in title. */
export function IdCell({ value, short = true, tone = 'mute' }: { value: string | null | undefined; short?: boolean; tone?: 'mute' | 'brand' | 'ink' }) {
  if (!value) return <span className="font-mono text-mute">--</span>;
  const cls = tone === 'brand' ? 'text-brand-text' : tone === 'ink' ? 'text-ink' : 'text-mute';
  return (
    <span title={value} className={`font-mono text-xs-plus ${cls}`}>
      {short ? shortId(value) : value}
    </span>
  );
}

/** Numeric cell: tabular, right aligned. */
export function Num({ value, className = '' }: { value: number | null | undefined; className?: string }) {
  return <span className={`block text-right font-mono tabular-nums ${className}`}>{value === null || value === undefined ? '--' : value.toLocaleString('en-US')}</span>;
}

/** Filter bar: a row of compact controls above a table. */
export function FilterBar({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 border-b border-hair px-5 py-3">
      <div className="flex flex-wrap items-end gap-3">{children}</div>
      {right ? <div className="flex items-center gap-2">{right}</div> : null}
    </div>
  );
}

export function FilterSelect({ label, value, onChange, options, allLabel = 'All', width = 160 }: { label: string; value: string | undefined; onChange: (v: string | undefined) => void; options: SelectOption[]; allLabel?: string; width?: number }) {
  return (
    <label className="flex flex-col gap-1" style={{ width }}>
      <span className="label-mono">{label}</span>
      <Select value={value ?? '__all'} onValueChange={(v) => onChange(v === '__all' ? undefined : v)} options={[{ value: '__all', label: allLabel }, ...options]} aria-label={label} />
    </label>
  );
}

export function FilterInput({ label, value, onChange, placeholder, mono = false, width = 200, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; mono?: boolean; width?: number; type?: string }) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (local !== value) onChange(local);
    }, 350);
    return () => window.clearTimeout(t);
  }, [local, value, onChange]);
  return (
    <label className="flex flex-col gap-1" style={{ width }}>
      <span className="label-mono">{label}</span>
      <Input type={type} value={local} onChange={(e) => setLocal(e.target.value)} placeholder={placeholder} mono={mono} aria-label={label} />
    </label>
  );
}

/** Load-more footer for cursor pages. */
export function LoadMore({ hasMore, loading, onLoadMore }: { hasMore: boolean; loading: boolean; onLoadMore: () => void }) {
  if (!hasMore) return null;
  return (
    <Button variant="tertiary" size="sm" loading={loading} onClick={onLoadMore}>
      Load more
    </Button>
  );
}

/**
 * Button for a sensitive action (recent authentication required). When the recent-auth window is
 * stale the button becomes `Re-authenticate to continue` and starts the login flow instead of
 * failing silently.
 */
export function SensitiveButton({ children, onClick, ...rest }: ButtonProps) {
  const session = useSession();
  if (!session.recentAuthFresh) {
    return (
      <Button {...rest} variant={rest.variant ?? 'tertiary'} icon={undefined} loading={false} onClick={() => session.reauthenticate()}>
        Re-authenticate to continue
      </Button>
    );
  }
  return (
    <Button {...rest} onClick={onClick}>
      {children}
    </Button>
  );
}

/** Confirmation dialog for destructive or irreversible actions. */
export function ConfirmDialog({ open, onOpenChange, title, subtitle, body, confirmLabel, onConfirm, busy = false, sensitive = true, destructive = false }: { open: boolean; onOpenChange: (open: boolean) => void; title: string; subtitle?: string; body: ReactNode; confirmLabel: string; onConfirm: () => void | Promise<void>; busy?: boolean; sensitive?: boolean; destructive?: boolean }) {
  const Confirm = sensitive ? SensitiveButton : Button;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      subtitle={subtitle}
      width={460}
      actions={
        <>
          <Button variant="tertiary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Confirm variant="primary" loading={busy} onClick={() => void onConfirm()} className={destructive ? '' : undefined}>
            {confirmLabel}
          </Confirm>
        </>
      }
    >
      <div className="text-[13px] leading-relaxed text-ink/85">{body}</div>
    </Dialog>
  );
}

/** JSON textarea with parse feedback. */
export function JsonEditor({ value, onChange, rows = 18, label = 'Body (JSON)', error }: { value: string; onChange: (v: string) => void; rows?: number; label?: string; error?: string | null }) {
  const parseError = useMemo(() => {
    try {
      JSON.parse(value);
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : 'invalid JSON';
    }
  }, [value]);
  return (
    <Field label={label} error={error ?? parseError ?? undefined} hint={parseError ? 'INVALID' : 'VALID JSON'}>
      <Textarea mono rows={rows} value={value} onChange={(e) => onChange(e.target.value)} spellCheck={false} className="min-h-[240px] text-xs-plus leading-relaxed" />
    </Field>
  );
}

/** Link to a person by id, resolving the display name from the people list cache when possible. */
export function UserLink({ userId, fallback }: { userId: string | null | undefined; fallback?: string }) {
  const session = useSession();
  const enabled = Boolean(userId) && session.has(['people.manage', 'audit.read', 'account.assign']);
  const people = useApi('admin.people.list', { input: { limit: 200 } }, { enabled });
  if (!userId) return <span className="text-mute">--</span>;
  if (userId === '00000000-0000-0000-0000-000000000000') return <span className="text-mute">System</span>;
  const person = people.data?.items.find((u) => u.id === userId);
  const label = person?.displayName ?? fallback ?? shortId(userId);
  if (!enabled) return <span title={userId}>{label}</span>;
  return (
    <Link to="/people/$userId" params={{ userId }} className="text-ink hover:underline" title={userId}>
      {label}
    </Link>
  );
}

export function AccountLink({ accountId, fallback }: { accountId: string | null | undefined; fallback?: string }) {
  const session = useSession();
  const enabled = Boolean(accountId) && session.has(['account.assign', 'account.register', 'audit.read']);
  const accounts = useApi('admin.accounts.list', { input: { limit: 200 } }, { enabled });
  if (!accountId) return <span className="text-mute">--</span>;
  const account = accounts.data?.items.find((a) => a.id === accountId);
  const label = account?.displayName ?? fallback ?? shortId(accountId);
  if (!enabled) return <span title={accountId}>{label}</span>;
  return (
    <Link to="/accounts/$accountId" params={{ accountId }} className="text-ink hover:underline" title={accountId}>
      {label}
    </Link>
  );
}

export function RoleChips({ roles }: { roles: readonly api.User['roles'][number][] }) {
  if (roles.length === 0) return <span className="text-mute">--</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <Chip key={r} tone="mute" title={r}>
          {r === 'agency_administrator' ? 'Admin' : r === 'forward_deployed_operator' ? 'Operator' : r === 'template_editor' ? 'Editor' : r === 'template_publisher' ? 'Publisher' : 'Auditor'}
        </Chip>
      ))}
    </span>
  );
}

/** Capability gate: renders a one-sentence empty state when the session lacks the capability. */
export function RequireCapability({ capability, children }: { capability: api.RouteDef['capability']; children: ReactNode }) {
  const session = useSession();
  if (capability && !session.has(capability)) {
    return (
      <Page>
        <EmptyState message="Your capabilities do not include this area." />
      </Page>
    );
  }
  return <>{children}</>;
}

/** Row action that navigates on activate. */
export function useOpen() {
  const navigate = useNavigate();
  return (to: string) => void navigate({ to });
}

export function TableCard({ title, subtitle, rightSlot, children, span = 'col-span-12', footer }: { title: string; subtitle?: string; rightSlot?: ReactNode; children: ReactNode; span?: string; footer?: ReactNode }) {
  return (
    <section className={`${span} flex flex-col rounded-sm border border-line bg-panel`}>
      <header className="flex items-center justify-between gap-4 border-b border-hair px-5 py-3">
        <div className="min-w-0">
          <h3 className="text-[13px] font-medium tracking-tight text-ink">{title}</h3>
          {subtitle ? <p className="label-mono mt-0.5">{subtitle}</p> : null}
        </div>
        {rightSlot ? <div className="flex shrink-0 items-center gap-2">{rightSlot}</div> : null}
      </header>
      {children}
      {footer ? <div className="border-t border-hair px-5 py-3">{footer}</div> : null}
    </section>
  );
}
